// =============================================================================
// SIREPPAR - Dropdown de versiones anteriores de una rendición (anual o campaña)
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronDownIcon, DocumentTextIcon, EyeIcon } from '@heroicons/react/24/outline';

interface Version {
  id: string;
  numeroVersion: number;
  hashCode: string;
  fechaGeneracion: string;
  pdfGeneradoUrl: string | null;
  esBorrador?: boolean;
  usuario?: {
    nombre: string;
    apellido: string;
    email: string;
  };
}

interface VersionesService {
  listVersiones: (id: string) => Promise<{ data: Version[] }>;
  descargarVersionPdf: (id: string, numeroVersion: number) => Promise<{ data: { url: string } }>;
}

interface Props {
  rendicionId: string;
  versionActual: number;
  service: VersionesService;
  queryKey: string;
  /** Click en una versión: precarga el wizard con ese snapshot. */
  onVerVersion: (numeroVersion: number) => void;
  /** Número de versión actualmente cargada como snapshot (para resaltar). */
  versionEnVista?: number | null;
}

function formatFecha(f: string): string {
  return new Date(f).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function VersionesDropdown({
  rendicionId,
  versionActual,
  service,
  queryKey,
  onVerVersion,
  versionEnVista,
}: Props) {
  const [open, setOpen] = useState(false);
  const [descargandoNum, setDescargandoNum] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: [queryKey, rendicionId],
    queryFn: async () => {
      const res = await service.listVersiones(rendicionId);
      return (res.data ?? []) as Version[];
    },
    enabled: open,
    staleTime: 0,
  });

  const versiones = data ?? [];

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  async function abrirPdf(numeroVersion: number) {
    try {
      setDescargandoNum(numeroVersion);
      const res = await service.descargarVersionPdf(rendicionId, numeroVersion);
      const url = res.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('No se pudo obtener la URL del PDF');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo abrir el PDF');
    } finally {
      setDescargandoNum(null);
    }
  }

  function verVersion(numeroVersion: number) {
    onVerVersion(numeroVersion);
    setOpen(false);
  }

  if (versionActual < 1) return null;

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
      >
        Ver anteriores
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-20">
          <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-700">
            Historial de versiones
          </div>

          {isLoading ? (
            <div className="px-3 py-3 text-xs text-gray-500 text-center">Cargando...</div>
          ) : versiones.length === 0 ? (
            <div className="px-3 py-3 text-xs text-gray-500 text-center">
              No hay versiones generadas.
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {versiones.map((v) => {
                const esActual = v.numeroVersion === versionActual;
                const enVista = versionEnVista === v.numeroVersion;
                const cargandoPdf = descargandoNum === v.numeroVersion;
                return (
                  <li
                    key={v.id}
                    className={`px-3 py-2 ${enVista ? 'bg-amber-50' : esActual ? 'bg-blue-50/50' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <DocumentTextIcon className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900 text-xs">
                            Versión N° {v.numeroVersion}
                          </span>
                          {v.esBorrador && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">
                              borrador
                            </span>
                          )}
                          {esActual && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                              actual
                            </span>
                          )}
                          {enVista && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">
                              viendo
                            </span>
                          )}
                        </div>
                        <div className="text-gray-500 text-[11px] mt-0.5">
                          {formatFecha(v.fechaGeneracion)}
                        </div>
                        {v.usuario && (
                          <div className="text-gray-400 text-[11px] truncate">
                            {v.usuario.nombre} {v.usuario.apellido}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1.5">
                          <button
                            type="button"
                            onClick={() => verVersion(v.numeroVersion)}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800"
                          >
                            <EyeIcon className="w-3.5 h-3.5" />
                            Ver datos
                          </button>
                          <button
                            type="button"
                            onClick={() => abrirPdf(v.numeroVersion)}
                            disabled={cargandoPdf}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
                          >
                            <DocumentTextIcon className="w-3.5 h-3.5" />
                            {cargandoPdf ? 'Abriendo...' : 'PDF'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500">
            "Ver datos" abre el wizard con esa versión (solo lectura).
          </div>
        </div>
      )}
    </div>
  );
}
